import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import LoadingScreen from "../Utils/LoadingScreen";
import { Plus, Trash } from "lucide-react";
import useNotification from "../../Sevices/useNotification";
import useKYCImageUpload from "../../Sevices/Driver Api/useKYCImageUpload";
import { useGlobalContext } from "../../GlobalContext";

const ViewBanner = () => {
  const { fetchBanners, deleteBanner, insertBanner } = useNotification();

  const { uploadImageToServer } = useKYCImageUpload();
  const { AdminId } = useGlobalContext();

  const [Loading, setLoading] = useState(false);
  const [bannersData, setBannersData] = useState([]);
  const [isCreatingBanner, setIsCreatingBanner] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const [form, setForm] = useState({
    imageURL: "",
    imageTitle: "",
    imageDesctription: "",
    backgroundColor: "#ffffff",
    titleColor: "#000000",
    descriptionColor: "#666666",
  });

  useEffect(() => {
    fetchBanners(setBannersData, setLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm({
      imageURL: "",
      imageTitle: "",
      imageDesctription: "",
      backgroundColor: "#ffffff",
      titleColor: "#000000",
      descriptionColor: "#666666",
    });

    setSelectedImage("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDelete = (id) => {
    setSelectedDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedDeleteId) return;

    await deleteBanner(
      selectedDeleteId,
      () => fetchBanners(setBannersData, setLoading),
      setDeletingId
    );

    setShowDeleteModal(false);
    setSelectedDeleteId(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedDeleteId(null);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);

    try {
      const imageUrl = await uploadImageToServer(
        file,
        AdminId,
        "banner",
        `banner_${Date.now()}`
      );

      if (!imageUrl) {
        toast.error("Image upload failed");
        return;
      }

      setForm((prev) => ({
        ...prev,
        imageURL: imageUrl,
      }));

      setSelectedImage(imageUrl);
    } catch (err) {
      console.error("handleFileChange error:", err);
      toast.error("Error uploading image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.imageURL) {
      toast.error("Please select an image");
      return;
    }

    if (!form.imageTitle.trim()) {
      toast.error("Please enter banner title");
      return;
    }

    if (!form.imageDesctription.trim()) {
      toast.error("Please enter banner description");
      return;
    }

    await insertBanner(
      form,
      async () => {
        resetForm();
        setIsCreatingBanner(false);
        await fetchBanners(setBannersData, setLoading);
      },
      setLoading
    );
  };

  return (
    <div className="min-h-[80vh] p-6">
      {!isCreatingBanner && (
        <>
          <div className="flex items-end justify-end gap-4 mb-4">
            <button
              type="button"
              className="flex items-center space-x-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-xs shadow"
              onClick={() => setIsCreatingBanner(true)}
            >
              <Plus size={14} />
              <span>Create Banner</span>
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 w-full max-w-6xl mx-auto">
            <h2 className="text-2xl font-semibold text-center mb-6">
              All Banners
            </h2>

            {Loading ? (
              <LoadingScreen />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {bannersData.length > 0 ? (
                  bannersData.map((banner) => (
                    <div
                      key={banner.id}
                      className="rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all relative bg-white"
                      style={{
                        backgroundColor: banner.background_color || "#ffffff",
                      }}
                    >
                      {banner.image_url && (
                        <div className="relative w-full h-64 overflow-hidden">
                          <img
                            src={banner.image_url}
                            alt={banner.img_title || "Banner"}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      <div className="p-4 pb-14">
                        <h3
                          className="text-lg font-bold mb-2"
                          style={{
                            color: banner.title_color || "#000000",
                          }}
                        >
                          {banner.img_title || "-"}
                        </h3>

                        <p
                          className="text-sm"
                          style={{
                            color: banner.description_color || "#666666",
                          }}
                        >
                          {banner.image_description || "-"}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDelete(banner.id)}
                        disabled={deletingId === banner.id}
                        className="absolute bottom-3 right-3 bg-white/80 hover:bg-white p-2 rounded-full shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                        title="Delete banner"
                      >
                        <Trash size={18} className="text-red-600" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    No banners available
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {isCreatingBanner && (
        <div className="max-w-6xl mx-auto bg-white p-6 rounded-xl shadow">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-bold mb-4">Create Banner</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Banner Image
                </label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full p-2 border rounded"
                />

                {uploadingImage && (
                  <p className="text-sm text-gray-500 mt-1">
                    Uploading image...
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>

                <input
                  type="text"
                  name="imageTitle"
                  placeholder="Enter banner title"
                  value={form.imageTitle}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>

                <textarea
                  name="imageDesctription"
                  placeholder="Enter banner description"
                  value={form.imageDesctription}
                  onChange={handleChange}
                  rows={4}
                  className="w-full p-2 border rounded resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Background
                  </label>

                  <input
                    type="color"
                    name="backgroundColor"
                    value={form.backgroundColor}
                    onChange={handleChange}
                    className="w-full h-10 border rounded cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Title Color
                  </label>

                  <input
                    type="color"
                    name="titleColor"
                    value={form.titleColor}
                    onChange={handleChange}
                    className="w-full h-10 border rounded cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description Color
                  </label>

                  <input
                    type="color"
                    name="descriptionColor"
                    value={form.descriptionColor}
                    onChange={handleChange}
                    className="w-full h-10 border rounded cursor-pointer"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={Loading || uploadingImage}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white p-2 rounded disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {Loading ? "Creating..." : "Create Banner"}
                </button>

                <button
                  type="button"
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-black p-2 rounded"
                  onClick={() => {
                    resetForm();
                    setIsCreatingBanner(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Preview</h3>

              <div
                className="rounded-xl shadow-md overflow-hidden border min-h-[350px]"
                style={{
                  backgroundColor: form.backgroundColor || "#ffffff",
                }}
              >
                {selectedImage ? (
                  <div className="relative w-full h-64 overflow-hidden">
                    <img
                      src={selectedImage}
                      alt="Banner preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center bg-gray-100 text-gray-500 text-sm">
                    Image preview will appear here
                  </div>
                )}

                <div className="p-4">
                  <h3
                    className="text-lg font-bold mb-2"
                    style={{
                      color: form.titleColor || "#000000",
                    }}
                  >
                    {form.imageTitle || "Banner title"}
                  </h3>

                  <p
                    className="text-sm"
                    style={{
                      color: form.descriptionColor || "#666666",
                    }}
                  >
                    {form.imageDesctription ||
                      "Banner description will appear here."}
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-xl p-6 w-[300px] shadow-lg text-center">
            <h2 className="text-lg font-semibold mb-4">Are you sure?</h2>

            <p className="text-sm text-gray-500 mb-6">
              This banner will be permanently deleted.
            </p>

            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={cancelDelete}
                className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmDelete}
                disabled={deletingId === selectedDeleteId}
                className="px-4 py-2 rounded bg-red-500 hover:bg-red-600 text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deletingId === selectedDeleteId ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewBanner;